import { useTranslation } from 'react-i18next';
import { Subscription } from '@haccp/api-client';
import {
  getSubscriptionStatusClass,
  getSubscriptionStatusText,
  formatAdminDate,
  getDaysUntilExpiry,
  isSubscriptionExpiringSoon,
} from '../utils/admin';

interface SubscriptionListProps {
  subscriptions: Subscription[];
  isLoading?: boolean;
}

export default function SubscriptionList({ subscriptions, isLoading = false }: SubscriptionListProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-neutral-600">{t('adminList.loadingSubscriptions')}</div>
      </div>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-lg font-medium text-neutral-900">{t('adminList.noSubscriptions')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200">
            <th className="text-left px-4 py-3 font-medium text-neutral-700">{t('adminList.colStatus')}</th>
            <th className="text-left px-4 py-3 font-medium text-neutral-700">{t('adminList.colClient')}</th>
            <th className="text-left px-4 py-3 font-medium text-neutral-700">{t('adminList.colPeriodEnd')}</th>
            <th className="text-left px-4 py-3 font-medium text-neutral-700">{t('adminList.colDaysLeft')}</th>
            <th className="text-left px-4 py-3 font-medium text-neutral-700">{t('adminList.colCreated')}</th>
          </tr>
        </thead>
        <tbody>
          {subscriptions.map((sub) => {
            const daysLeft = getDaysUntilExpiry(sub);
            const expiringSoon = isSubscriptionExpiringSoon(sub);

            return (
              <tr
                key={sub.id}
                className={`border-b border-neutral-100 hover:bg-neutral-50 ${
                  expiringSoon && sub.status === 'ACTIVE' ? 'bg-yellow-50' : ''
                }`}
              >
                <td className="px-4 py-3">
                  <span
                    className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${getSubscriptionStatusClass(
                      sub.status
                    )}`}
                  >
                    {getSubscriptionStatusText(sub.status)}
                  </span>
                </td>
                <td className="px-4 py-3 text-neutral-700 font-medium">
                  {(sub as any).establishment?.name || '-'}
                </td>
                <td className="px-4 py-3 text-neutral-700">{formatAdminDate(sub.currentPeriodEnd)}</td>
                <td className="px-4 py-3">
                  <span className={daysLeft <= 0 ? 'text-red-600 font-medium' : 'text-neutral-600'}>
                    {daysLeft <= 0 ? t('adminList.expired') : t('adminList.daysLeft', { count: daysLeft })}
                  </span>
                </td>
                <td className="px-4 py-3 text-neutral-600">{formatAdminDate(sub.createdAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
